import * as React from 'react';
import cls from 'classnames';
import { Formik, Form, Field, FormikProps, FormikHelpers as FormikActions } from 'formik';

import PageOverlay from '../page-overlay';

import { WSAppContext, WSAppContextType, AuthFormData } from '../../providers/ws-app';
import { Auth, AuthProvider } from '@weddingspot/ws-api-client';

import styles from './AuthModal.module.scss';

const genericLoginError = 'An error occurred while attempting to log in, please try again later';
const resendLimitError = "You've reached the limit for resending the verification email. Please try again in an hour.";

export interface AuthModalFormData {
    email: string;
    password: string;
}

export interface Props {
}

interface State {
    loadFb: boolean;
    facebookError: string | undefined;
    unverifiedEmail: string | undefined;
    resendCooldown: number;
    resendLimitMessage: string | undefined;
    verificationSent: boolean;
}

export default class AuthModal extends React.Component<Props, State> {
    static contextType: React.Context<WSAppContextType> = WSAppContext;
    // @ts-ignore
    context!: React.ContextType<typeof WSAppContext>;

    private resendTimer: any = null;
    private wasOpen = false;

    constructor(props: Props) {
        super(props);

        this.handleCreateClick = this.handleCreateClick.bind(this);
        this.handleFormSubmit = this.handleFormSubmit.bind(this);
        this.renderFormField = this.renderFormField.bind(this);
        this.validateForm = this.validateForm.bind(this);

        this.state = {
            facebookError: undefined,
            loadFb: false,
            unverifiedEmail: undefined,
            resendCooldown: 0,
            resendLimitMessage: undefined,
            verificationSent: false,
        };

        this.handleResendVerification = this.handleResendVerification.bind(this);
    }

    componentWillUnmount() {
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
        }
    }

    componentDidMount() {
        this.wasOpen = this.context.authModalOpen;
        if (!this.state.loadFb && this.context.authModalOpen) {
            this.setState({ loadFb: true });
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (!prevState.loadFb && this.context.authModalOpen) {
            this.setState({ loadFb: true });
        }

        // The modal stays mounted while hidden, so clear the unverified banner on close
        // or it reappears on the next open. The resend cooldown keeps running.
        if (this.wasOpen && !this.context.authModalOpen && this.state.unverifiedEmail) {
            this.setState({ unverifiedEmail: undefined, resendLimitMessage: undefined, verificationSent: false });
        }
        this.wasOpen = this.context.authModalOpen;
    }

    public render() {
        const { closeAuthModal, authModalOpen, rootNodeId, isForceAuth } = this.context;

        return (
            <PageOverlay
                rootNodeId={rootNodeId}
                onClick={isForceAuth ? () => {} : closeAuthModal}
                isVisible={authModalOpen}
                testId='AuthModalPageOverlay'
            >
                <div className={styles.className}>
                    <div className={styles.header}>
                        <div className={styles.headline}>Sign In</div>
                        {!isForceAuth && (
                            <span
                                className={cls(styles.closeButton, 'icon', 'icon-hb-nx-close')}
                                onClick={closeAuthModal}
                                data-testid='AuthModal--closeButton'
                            />
                        )}
                    </div>
                    {this.renderUnverifiedBanner()}
                    <Formik
                        initialValues={{
                            email: '',
                            password: '',
                        }}
                        validate={this.validateForm}
                        onSubmit={this.handleFormSubmit}
                    >
                        {({ isSubmitting, errors, status, touched }: FormikProps<AuthModalFormData>) => (
                            <Form>
                                <div>
                                    <div className={styles.content}>
                                        <div>
                                            {this.renderFormField(
                                                'email',
                                                'email',
                                                'Email',
                                                !!touched.email && !!errors.email,
                                                errors.email,
                                                'Email'
                                            )}

                                            {this.renderFormField(
                                                'password',
                                                'password',
                                                'Password',
                                                !!touched.password && !!errors.password,
                                                errors.password,
                                                'Password'
                                            )}

                                            {status && <div className={styles.formLevelError}>{status}</div>}
                                        </div>
                                    </div>
                                </div>
                                <div className={cls(styles.footer, styles.content)}>
                                    <button
                                        type='submit'
                                        className={cls(styles.signInButton, 'nx-button', 'nx-button--primary')}
                                        disabled={isSubmitting}
                                    >
                                        Sign In
                                    </button>

                                    <div className={styles.footerLinks}>
                                        <div className={styles.footerLink}>
                                            <a href='#' onClick={this.handleCreateClick}>
                                                Create
                                            </a>
                                            {'\u00a0'}a Free Account
                                        </div>
                                        <div className={styles.footerLink}>
                                            <a href='/resetpassword/' className={styles.passwordResetLink}>
                                                Forgot password
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </PageOverlay>
        );
    }

    private renderFormField(fieldName: string, type: string, placeholder: string, hasError: boolean, errorMsg: string | undefined, label: string) {
        return (
            <div className={cls(styles.inputWrapper, { [styles.hasError]: hasError })}>
                <Field
                    className={cls(styles.input, { [styles.hasError]: hasError })}
                    name={fieldName}
                    type={type}
                    placeholder={placeholder}
                    aria-label={label}
                />
                {hasError && <div className={styles.fieldError}>{errorMsg}</div>}
            </div>
        );
    }

    private validateForm(values: AuthModalFormData) {
        let errors: Partial<AuthModalFormData> = {};
        if (!values.email) {
            errors.email = 'Please enter an email';
        } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (!values.password) {
            errors.password = 'Please enter a password';
        }
        return errors;
    }

    private handleFormSubmit(values: AuthFormData, actions: FormikActions<AuthFormData>) {
        // Clear the error and banner from an earlier attempt so they can't sit next to this attempt's result.
        actions.setStatus(undefined);
        this.stopResendCooldown();
        this.setState({ unverifiedEmail: undefined, resendLimitMessage: undefined, verificationSent: false });
        this.context
            .signIn(values)
            .then(() => {
                this.context
                    .loadUserData()
                    .then(() => {
                        this.context.closeAuthModal();
                        this.context.setIsForceAuth(false);
                    })
                    .catch(() => {
                        actions.setStatus(genericLoginError);
                    })
                    .then(() => {
                        actions.setSubmitting(false);
                    });
            })
            .catch((err: any) => {
                const content = err && err.detail && err.detail.content;
                if (content && content.error_type === 'unverified') {
                    // No resends left this hour: show the limit message instead of the link.
                    const noResendsLeft = content.resends_remaining === 0;
                    this.setState({
                        unverifiedEmail: content.email || values.email,
                        resendLimitMessage: noResendsLeft ? resendLimitError : undefined,
                        // A running resend wait means a link was sent in the last 90s.
                        verificationSent: content.resend_wait_seconds > 0,
                    });
                    // Continue the wait from the last email sent (e.g. at signup), so a
                    // new sign-in attempt can't skip it.
                    if (!noResendsLeft && content.resend_wait_seconds > 0) {
                        this.startResendCooldown(content.resend_wait_seconds);
                    }
                } else if (err && err.status === 429) {
                    const msg = content && content.detail;
                    actions.setStatus(msg || 'Too many requests, try again later');
                } else {
                    actions.setStatus('Invalid email or password, please try again');
                }
                actions.setSubmitting(false);
            });
    }

    private renderUnverifiedBanner() {
        const { unverifiedEmail, resendCooldown, resendLimitMessage, verificationSent } = this.state;
        if (!unverifiedEmail) {
            return null;
        }

        const resendClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            this.handleResendVerification();
        };

        // Not sent yet: "here" is a link inside the text and the link after it is hidden.
        // Sent: the text says so and the link or countdown follows it.
        if (!verificationSent) {
            return (
                <div className={styles.unverifiedBanner} data-testid='AuthModal--unverifiedBanner'>
                    <span>
                        This email is not verified yet.
                        {!resendLimitMessage && (
                            <>
                                {' '}Click{' '}
                                <a href='#' className={styles.unverifiedResendLink} onClick={resendClick}>
                                    here
                                </a>
                                {' '}to request a verification link.
                            </>
                        )}
                    </span>
                    {resendLimitMessage && <div className={styles.unverifiedBannerSent}>{resendLimitMessage}</div>}
                </div>
            );
        }

        return (
            <div className={styles.unverifiedBanner} data-testid='AuthModal--unverifiedBanner'>
                <span>
                    We&apos;ve sent you a verification link. Please verify within 24 hours.&nbsp;
                </span>
                {resendLimitMessage ? (
                    <div className={styles.unverifiedBannerSent}>{resendLimitMessage}</div>
                ) : resendCooldown > 0 ? (
                    <span className={styles.unverifiedResendCooldown}>Resend in {resendCooldown}s</span>
                ) : (
                    <a
                        href='#'
                        className={styles.unverifiedResendLink}
                        onClick={resendClick}
                    >
                        Resend email
                    </a>
                )}
            </div>
        );
    }

    private handleResendVerification() {
        const { unverifiedEmail } = this.state;
        if (!unverifiedEmail) {
            return;
        }

        // Switch to the sent text with the countdown right away, like the countdown itself.
        this.setState({ verificationSent: true });
        this.startResendCooldown(90);
        AuthProvider.resendVerification(unverifiedEmail)
            .then((res: any) => {
                if (res && res.resends_remaining === 0) {
                    // That was the last resend this hour: disable further resends now.
                    this.stopResendCooldown();
                    this.setState({ resendLimitMessage: resendLimitError });
                }
            })
            .catch((err: any) => {
                if (err && err.status === 429) {
                    // Resend limit reached: nothing was sent, so stop the countdown and say when to retry.
                    const content = err.detail && err.detail.content;
                    this.stopResendCooldown();
                    this.setState({
                        verificationSent: false,
                        resendLimitMessage: (content && content.detail) || resendLimitError,
                    });
                }
            });
    }

    private stopResendCooldown() {
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
            this.resendTimer = null;
        }
        this.setState({ resendCooldown: 0 });
    }

    private startResendCooldown(seconds: number) {
        if (this.resendTimer) {
            clearInterval(this.resendTimer);
        }
        this.setState({ resendCooldown: seconds });
        this.resendTimer = setInterval(() => {
            this.setState((prev) => {
                if (prev.resendCooldown <= 1) {
                    clearInterval(this.resendTimer);
                    this.resendTimer = null;
                    return { resendCooldown: 0 };
                }
                return { resendCooldown: prev.resendCooldown - 1 };
            });
        }, 1000);
    }

    private handleCreateClick(e: React.SyntheticEvent<HTMLAnchorElement>) {
        e.preventDefault();
        this.context.openSignupModal();
    }
}
